import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Certificate from "../component/Certificate";
import { getKbarDetails } from "./core/_requests";
import {KBAR, kbarInitialValue} from "./core/_models";
import { Contract } from "ethers";
import ABI from "../config/ABI_V2.json";
import DuBois from "../assets/du-bois";
import Disclaimer from "../component/Disclaimer";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc'
import {ReadContractErrors} from "../config/constants";
import {useProvider} from "./core/useProvider";

dayjs.extend(utc);

const CertificatePage = () => {
  const params = useParams();
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [data, setData] = useState<KBAR>(kbarInitialValue);
  const [redeemed, setRedeemed] = useState<boolean>(false)

  const { provider, initProviderPending } = useProvider()

  const loading = detailsLoading || initProviderPending

  const getTokenOwner = useCallback(
    async (tokenId: string | null, kbarDetails: KBAR) => {
      const contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS;

      if (contractAddress && Boolean(tokenId) && provider) {
        try {
          const contract = new Contract(contractAddress, ABI, provider);
          const ownerResponse = await contract.ownerOf(tokenId);
          setData({ ...kbarDetails, ownerAddress: ownerResponse });
        } catch (error: any) {
          if (error?.message.includes(ReadContractErrors.RedeemedToken)) {
            setRedeemed(true)
          }
        }
      } else {
        setData({ ...kbarDetails, ownerAddress: kbarDetails?.receiver_address });
      }
    },
    [provider]
  );

  const getDetails = useCallback(async () => {
    if (!params?.serial_number) return;

    setDetailsLoading(true);
    setImageLoading(true);

    try {
      const kbarDetails = await getKbarDetails(params?.serial_number);
      setData(kbarDetails);
      if (kbarDetails) {
        await getTokenOwner(kbarDetails?.tokenId, kbarDetails);
      }
    } catch (error) {
      console.error("Failed to load kbar details", error);
    } finally {
      setDetailsLoading(false);
    }
  }, [getTokenOwner, params?.serial_number]);

  useEffect(() => {
    (async () => {
      if (!initProviderPending) {
        await getDetails()
      }
    })()
  }, [getDetails, initProviderPending])

  return (
    <>
      <div className="container">
        {redeemed ? (
          <div className="redeemed-screen">
            <h1>This token is no longer active</h1>
          </div>
        ) : (
          <div
            className={`certificate-section ${
              loading || imageLoading ? "sub-container" : ""
            }`}
          >
            <div className="certificate-section2">
              <div className="goldbar-image-section">
                {loading ? (
                  <p className="skeleton skeleton-kbar-image" />
                ) : (
                  <img
                    src={data?.image}
                    alt="gold-bar"
                    className="gold-bar"
                    onLoad={() => setImageLoading(false)}
                  />
                )}
              </div>
              <Certificate loading={loading} data={data} />
            </div>
            <div className="owner-section">
              <div className="owner-info-before" />
              <div className="owner-info-after">
                {loading ? (
                  <div className="owner-details ">
                    <p className="owner-id skeleton-info-text"></p>
                    <p className="owner-id skeleton-info-text"></p>
                    <p className="owner-details skeleton-info-text"></p>
                  </div>
                ) : (
                  <div className="owner-details">
                    <p className="owner-info-value">Currently held by</p>
                    <p className="owner-id owner-info-value">
                      {data?.ownerAddress || data?.receiver_address}
                    </p>
                    {data?.ownerAddress || data?.receiver_address ? (
                      <p className="owner-info-value">{`on Ethereum as at ${dayjs()
                        .utc()
                        .format("hh:mma (UTC), DD MMMM YYYY")
                        .toString()}`}</p>
                    ) : (
                      <p className="owner-info-value">
                        <br />
                      </p>
                    )}
                  </div>
                )}
                <DuBois />
              </div>
            </div>
            <div className="content-separator" />
            <Disclaimer />
          </div>
        )}
      </div>
    </>
  );
};

export default CertificatePage;
